// Utilities for parsing and matching ride height "suspension type" encodings
// embedded in file names and RH repo states.
//
// Encoding format: a sign character ('p' for positive, 'm' for negative)
// followed by digits. The digits represent the magnitude with an implied
// decimal point after the first digit, so the number of decimal places equals
// (digits length - 1). The encoded magnitude is the actual attitude value
// rounded to that many decimal places. Examples:
//   0.5      -> p050    (0.50)
//   -0.05    -> m005    (0.05)
//   1.5      -> p150    (1.50)
//   -3       -> m300    (3.00)
//   1.75     -> p175    (1.75)
//   9.34     -> p934    (9.34)
//   1.62505  -> p162505 (full precision) or p1625 (rounded to 3 decimals)
//
// Some file names omit the leading integer "0" for magnitudes < 1, so the
// implied decimal point sits before all digits instead of after the first one
// (e.g. `p934` -> 0.934, while `p175` -> 1.75 and `m050` -> 0.50). Matching
// therefore accepts either decimal placement.
//
// Because a file may encode fewer significant digits than the stored attitude
// value, matching rounds the actual value to the precision present in the
// encoding before comparing. This keeps the comparison tolerant of how many
// trailing digits a given file name happens to carry.

/**
 * Extract the four leading sign+digit components (FRH, RRH, Roll, Yaw) from an
 * encoded string such as a file name (`p162505_m175_p934_p050_...`) or an
 * RH repo state's `suspensionTypeEncoding`.
 *
 * @returns the four components, or `null` when the value is not encoded.
 */
export const parseRHEncodingComponents = (value: string): string[] | null => {
  if (!value) return null
  const match = value.match(/^([pm]\d+)_([pm]\d+)_([pm]\d+)_([pm]\d+)/)
  return match ? [match[1], match[2], match[3], match[4]] : null
}

/**
 * Determine whether a single encoded component (e.g. `p162505` or `p1625`)
 * represents the given attitude value. The component's digit count defines the
 * precision; the actual value is rounded to that precision before comparing, so
 * a higher-precision value still matches a shorter encoding.
 *
 * The encoded digits omit the decimal point, and file names use two
 * conventions for where it is implied:
 *   - after the first digit, for magnitudes that include an integer digit
 *     (e.g. `p175` -> 1.75, `m050` -> 0.50) — decimals = (digit count - 1)
 *   - before all digits, for magnitudes < 1 written without the leading zero
 *     (e.g. `p934` -> 0.934) — decimals = (digit count)
 * The value matches when rounding it to *either* precision reproduces the
 * encoded magnitude.
 */
export const rhComponentMatchesValue = (component: string, value: number): boolean => {
  if (!component || component.length < 2) return false

  const sign = component[0] === 'm' ? -1 : 1
  const numStr = component.slice(1)
  if (!/^\d+$/.test(numStr)) return false

  const encoded = parseInt(numStr, 10)
  const absValue = Math.abs(value)

  // A zero magnitude matches a zero value regardless of sign.
  if (encoded === 0) return Math.round(absValue * Math.pow(10, numStr.length - 1)) === 0

  // Sign must agree for non-zero magnitudes.
  if (value > 0 && sign === -1) return false
  if (value < 0 && sign === 1) return false

  // Try both decimal-point conventions; the value matches if either precision
  // reproduces the encoded magnitude when the value is rounded to it.
  for (const decimals of [numStr.length - 1, numStr.length]) {
    const scale = Math.pow(10, decimals)
    if (Math.round(absValue * scale) === encoded) return true
  }

  return false
}
